import { salvarRascunho, carregarRascunho } from './db';
import type { DraftData, MeioItem, MeioUfcItem } from './types';
import { atualizarChecklistMeios, hasContent, debounceKey } from './progress';

const LIST_SEL = '#meios-lista';
const ADD_BTN_SEL = '#add-meio';
const LIST_SEL_UFC = '#meios-ufc-lista';
const ADD_BTN_SEL_UFC = '#add-meio-ufc';

function el<T extends Element = Element>(sel: string, root: ParentNode | Document = document): T | null {
    return root.querySelector(sel) as T | null;
}
function qsa<T extends Element = Element>(sel: string, root: ParentNode | Document = document): T[] {
    return Array.from(root.querySelectorAll(sel)) as T[];
}

function editorHTML(id: string): string {
    const cont = document.getElementById(id);
    const ql = cont?.querySelector('.ql-editor') as HTMLElement | null;
    return ql?.innerHTML ?? '';
}

function criarLinha(init?: MeioItem): HTMLDivElement {
    const row = document.createElement('div');
    row.className = 'list-item';
    row.innerHTML = `
    <input type="text" class="inp-nome" placeholder="Nome completo" />
    <input type="text" class="inp-ident" placeholder="SIAPE/CPF" />
    <input type="text" class="inp-cargo" placeholder="Cargo" />
    <input type="text" class="inp-funcao" placeholder="Função" />
    <button type="button" class="remove-button">Remover</button>
  `;

    const $nome = row.querySelector<HTMLInputElement>('.inp-nome')!;
    const $ident = row.querySelector<HTMLInputElement>('.inp-ident')!;
    const $cargo = row.querySelector<HTMLInputElement>('.inp-cargo')!;
    const $func = row.querySelector<HTMLInputElement>('.inp-funcao')!;
    const $rm = row.querySelector<HTMLButtonElement>('.remove-button')!;

    if (init) {
        $nome.value = init.nome ?? '';
        $ident.value = init.identificacao ?? '';
        $cargo.value = init.cargo ?? '';
        $func.value = init.funcao ?? '';
    }

    const onChange = () => {
        debounceKey('meios-change', () => {
            void persistir();
            void syncProgressoMeios();
        }, 120);
    };

    [$nome, $ident, $cargo, $func].forEach(inp => inp.addEventListener('input', onChange));
    $rm.addEventListener('click', () => { row.remove(); void persistir(); void syncProgressoMeios(); });

    return row;
}

function criarLinhaUfc(item: any = {}) {
    const row = document.createElement('div');
    row.className = 'list-item';

    row.innerHTML = `
    <input class="inp-nome" placeholder="Nome completo" value="${item.nome ?? ''}" />
    <input class="inp-ident" placeholder="SIAPE/CPF" value="${item.identificacao ?? ''}" />
    <input class="inp-email" placeholder="Email" value="${item.email ?? ''}" />
    <input class="inp-fone" placeholder="Telefone"  value="${item.fone ?? ''}" />
    <input class="inp-funcao" placeholder="Função" value="${item.funcao ?? ''}" />
    <button type="button" class="remove-button">Remover</button>
  `;

    const $nome = row.querySelector<HTMLInputElement>('.inp-nome')!;
    const $ident = row.querySelector<HTMLInputElement>('.inp-ident')!;
    const $email = row.querySelector<HTMLInputElement>('.inp-email')!;
    const $fone = row.querySelector<HTMLInputElement>('.inp-fone')!;
    const $func = row.querySelector<HTMLInputElement>('.inp-funcao')!;
    const $rm = row.querySelector<HTMLButtonElement>('.remove-button')!;

    const scheduleSave = () => debounceKey(
        'meiosUfc-change',
        async () => {
            await persistir();
            await syncProgressoMeios();
        },
        250
    );

    [$nome, $ident, $email, $fone, $func].forEach(inp =>
        inp.addEventListener('input', scheduleSave)
    );

    $rm.addEventListener('click', () => {
        row.remove();
        void persistir();
        void syncProgressoMeios();
    });

    return row;
}

function coletar(listSelector: string = LIST_SEL): MeioItem[] {
    const list = el<HTMLDivElement>(listSelector);
    if (!list) return [];
    return qsa<HTMLDivElement>('.list-item', list)
        .map(row => {
            const g = (s: string) => (row.querySelector<HTMLInputElement>(s)?.value ?? '').trim();
            return { nome: g('.inp-nome'), identificacao: g('.inp-ident'), cargo: g('.inp-cargo'), funcao: g('.inp-funcao') };
        })
        .filter(m => m.nome || m.identificacao || m.cargo || m.funcao);
}

function coletarUfc(): MeioUfcItem[] {
    const list = el<HTMLDivElement>('#meios-ufc-lista');
    if (!list) return [];

    return qsa<HTMLDivElement>('.list-item', list)
        .map(row => {
            const g = (s: string) => (row.querySelector<HTMLInputElement>(s)?.value ?? '').trim();
            return {
                nome: g('.inp-nome'),
                identificacao: g('.inp-ident'),
                email: g('.inp-email'),
                fone: g('.inp-fone'),
                funcao: g('.inp-funcao'),
            };
        })
        .filter(m => m.nome || m.identificacao || m.email || m.fone || m.funcao);
}

export function getMeiosUfc(): MeioUfcItem[] {
    return coletarUfc();
}

async function persistir() {
    const atual = (await carregarRascunho()) ?? ({} as Partial<DraftData>);

    const payload: DraftData = {
        ...(atual as DraftData),

        meios: getMeios(),
        meiosUfc: getMeiosUfc(),

        objetivosGerais: atual?.objetivosGerais ?? '',
        objetivosEspecificos: atual?.objetivosEspecificos ?? '',
        justificativa: atual?.justificativa ?? '',
        responsabilidadesUFC: atual?.responsabilidadesUFC ?? '',
        responsabilidadesParceiro: atual?.responsabilidadesParceiro ?? '',
        atividadesConjuntas: atual?.atividadesConjuntas ?? '',
        direitosPI: atual?.direitosPI ?? '',

        recursosFinanceiros: atual?.recursosFinanceiros ?? '',
        infraestrutura: atual?.infraestrutura ?? '',

        tabelas_orcamento: Array.isArray(atual?.tabelas_orcamento) ? atual!.tabelas_orcamento! : [],
        orcamento: Array.isArray(atual?.orcamento) ? atual!.orcamento! : [],
    };

    await salvarRascunho(payload);
}

export function getMeios(): MeioItem[] { return coletar(); }

export function preencherMeios(itens: MeioItem[] = []) {
    const list = el<HTMLDivElement>(LIST_SEL);
    if (!list) return;
    list.innerHTML = '';
    (itens.length ? itens : [{}]).forEach(m => list.appendChild(criarLinha(m)));
}

export function preencherMeiosUfc(itens: MeioUfcItem[] = []) {
    const list = el<HTMLDivElement>('#meios-ufc-lista');
    if (!list) return;

    list.innerHTML = '';
    (itens.length ? itens : [{}]).forEach(m => list.appendChild(criarLinhaUfc(m)));
}

export function initMeiosLista() {
    const $add = el<HTMLButtonElement>(ADD_BTN_SEL);
    if ($add) {
        $add.addEventListener('click', () => {
            const list = el<HTMLDivElement>(LIST_SEL);
            if (!list) return;
            list.appendChild(criarLinha());
            void persistir();
            void syncProgressoMeios();
        });
    }
    const $addUfc = el<HTMLButtonElement>(ADD_BTN_SEL_UFC);
    if ($addUfc) {
        $addUfc.addEventListener('click', () => {
            const list = el<HTMLDivElement>(LIST_SEL_UFC);
            if (!list) return;
            list.appendChild(criarLinhaUfc());
            void persistir();
            void syncProgressoMeios();
        });
    }
}

export async function syncProgressoMeios() {
    const possuiMeiosPriv = getMeios().length > 0;
    const temRecursos = hasContent(editorHTML('recursosFinanceiros'));
    const temInfra = hasContent(editorHTML('infraestrutura'));

    atualizarChecklistMeios(!!(possuiMeiosPriv && temRecursos && temInfra));
}
